import { ThunkDispatch } from 'redux-thunk';
import { Action } from 'redux';
import { Auth } from '@justgiving/auth';
import { ERROR_MESSAGES } from '../../consts';
import { IAppState } from '../../types/state.d';
import { getTeamGuid } from '../../selectors/apiSelectors';
import { locksSelector } from '../../selectors/locksSelectors';
import { redirectByResponseStatus } from '../../utils';
import TeamLockTypes from '../../consts/lockTypes';
import {
  updateLocksRequestMade,
  updateLocksRequestError,
  updateLocksRequestSuccess,
} from '../actionCreators/apiActions';
import client, { hasUnauthenticatedError } from '../../apolloClient';
import {
  ProductType,
  UpdateRelationshipInput,
  UpdateRelationshipsMutation,
  UpdateRelationshipsMutationVariables,
} from '../../../types/graphql';
import UpdateRelationships from '../../queries/updateRelationshipsMutuation.gql';

const updateTeamLocks = (lockType: TeamLockTypes) => async (
  dispatch: ThunkDispatch<IAppState, Auth, Action>,
  getState: () => IAppState,
  auth: Auth,
) => {
  try {
    await auth.refreshAccessTokenIfExpired();
    dispatch(updateLocksRequestMade());
    const state = getState();
    const teamGuid = getTeamGuid(state) as string;
    const teamLocks = locksSelector(state);

    const newLocks: { id: string; type: string }[] =
      lockType === 'charity' || lockType === 'event'
        ? teamLocks.filter((el) => el.type !== lockType)
        : [];

    const relationships: UpdateRelationshipInput[] = teamLocks.map((lock) => {
      return {
        pageId: lock.legacyId,
        productType: getProductTypeFromLockType(lock.type),
        updateFields: {
          requiredForChildren: newLocks.some((x) => x.id === lock.id),
        },
      };
    });

    const { errors } = await client.mutate<
      UpdateRelationshipsMutation,
      UpdateRelationshipsMutationVariables
    >({
      mutation: UpdateRelationships,
      variables: {
        input: {
          pageId: teamGuid,
          relationships: relationships,
        },
      },
    });

    if (errors && hasUnauthenticatedError(errors)) {
      redirectByResponseStatus(401);
    }

    if (errors) {
      throw new Error(ERROR_MESSAGES.UPDATE_LOCK_ERROR);
    }

    dispatch(updateLocksRequestSuccess({ locks: newLocks }));
  } catch (error) {
    dispatch(updateLocksRequestError());
    throw error;
  }
};

function getProductTypeFromLockType(lockType: string): ProductType {
  switch (lockType) {
    case 'charity':
      return ProductType.Charity;
    case 'campaign':
      return ProductType.Campaign;
    case 'event':
      return ProductType.Event;
    default:
      throw new Error(
        `Invalid lock type. ${lockType} has no equivalent ES product type`,
      );
  }
}

export default updateTeamLocks;
